import React, { useEffect, useState, useCallback } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { withCookies } from "react-cookie"
import { useIntl } from "gatsby-plugin-react-intl"

import Lightbox from "../Lightbox"
import FormContentUnlock, {
  FileInfo as StyledFileInfo,
} from "../FormContentUnlock"
import Button from "../Button"
import Snippet from "../Snippet"

import { media, mixins } from "../../styles"
import { trackEvent } from "../../helpers/tracking"
import { knowledgeHubCookieName } from "../../constants"
import DownloadSVG from "../../images/icons/arrow-download.inline.svg"

const FormWrapper = styled.div`
  background: ${({ theme }) => theme.white};
  padding: 0 10px 30px;

  @media ${media.md} {
    padding: 0 35px 60px;
  }

  h2 {
    ${mixins.title4}
  }
`

const DownloadIcon = styled(DownloadSVG)`
  flex: none;
  margin-left: 8px !important;
  vertical-align: middle;
`

const FileInfo = styled(StyledFileInfo)`
  position: absolute;
  width: 100%;
`

const PublicationDownload = ({
  className,
  cookies,
  file,
  id,
  publicationType,
}) => {
  const [showDialog, setShowDialog] = useState(false)
  const [locked, setLocked] = useState(true)
  const cookieName = knowledgeHubCookieName
  const trackingCategory = "Publication"
  const intl = useIntl()

  const openLightbox = () => {
    setShowDialog(true)
  }

  const unlock = useCallback(() => {
    if (cookies.get(cookieName)) {
      setLocked(false)
    }
  }, [cookies, cookieName])

  const closeLightbox = () => {
    setShowDialog(false)
    unlock()
  }

  useEffect(() => {
    unlock()
  }, [cookies, unlock])

  // Generate ID to fetch Contentful Snippet according to content type
  // e.g., `downloadGuide`
  const translationPrefix = `download${
    publicationType.charAt(0).toUpperCase() + publicationType.slice(1)
  }`

  const trackOnDownloadClick = () => {
    trackEvent({
      category: trackingCategory,
      action: "Download",
      name: `Download | ${id} CTA | ${intl.locale.toUpperCase()}`,
    })
  }

  return (
    <div className={className}>
      {locked ? (
        <Button as="button" onClick={openLightbox} highlight>
          <Snippet id="downloadPublicationButton" field="linkText" />
          <DownloadIcon />
        </Button>
      ) : (
        <>
          <Button
            as="a"
            href={file.publicUrl}
            target="_blank"
            highlight
            onClick={() => trackOnDownloadClick()}
          >
            <Snippet id="downloadPublicationButton" field="linkText" />
            <DownloadIcon />
          </Button>
          {file?.publicUrl ? <FileInfo file={file} /> : null}
        </>
      )}

      <Lightbox
        ariaLabel={"Download"}
        close={closeLightbox}
        keepOpenOnOverlayClick={true}
        show={showDialog}
      >
        <FormWrapper>
          <FormContentUnlock
            leadSource="Whitepaper"
            cookieName={cookieName}
            confirmationButtonText={Snippet({
              id: `${translationPrefix}Form`,
              field: "linkText",
            })}
            file={file}
            header={Snippet({
              id: `${translationPrefix}Form`,
              field: "header",
            })}
            name={`${id} CTA`}
            publicationId={id}
            submitButtonText={Snippet({
              id: `${translationPrefix}`,
              field: "linkText",
            })}
            text={Snippet({
              id: `${translationPrefix}Form`,
              field: "text",
            })}
            trackingCategory="Publication"
          />
        </FormWrapper>
      </Lightbox>
    </div>
  )
}

PublicationDownload.propTypes = {
  className: PropTypes.string,
  file: PropTypes.shape({
    publicUrl: PropTypes.string.isRequired,
    file: PropTypes.shape({
      contentType: PropTypes.string.isRequired,
      details: PropTypes.shape({
        size: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  publicationType: PropTypes.string.isRequired,
}

export default withCookies(PublicationDownload)
